import { useMemo, useState } from "react";
import { rankingList } from "@/mocks/manga";

const sortOptions = [
  { key: "score", label: "综合评分" },
  { key: "readers", label: "追更人数" },
  { key: "rank", label: "官方排名" },
];

function readersToNumber(text: string) {
  return parseFloat(text.replace(/[^\d.]/g, "")) || 0;
}

export default function RankingSection() {
  const [sortKey, setSortKey] = useState("rank");

  const list = useMemo(() => {
    const copy = [...rankingList];
    if (sortKey === "score") {
      return copy.sort((a, b) => b.score - a.score);
    }
    if (sortKey === "readers") {
      return copy.sort((a, b) => readersToNumber(b.readers) - readersToNumber(a.readers));
    }
    return copy.sort((a, b) => a.rank - b.rank);
  }, [sortKey]);

  return (
    <section id="ranking" className="w-full bg-background-50 py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-label text-primary-600 uppercase tracking-widest">
              <i className="ri-trophy-line text-base" aria-hidden="true"></i>
              Ranking
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              人气漫画排行榜 Top 10
            </h2>
            <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
              这份榜单综合读者评分、追更人数与近两周讨论热度滚动计算，
              每一部都附带一句「它到底好在哪」的说明，方便你快速判断是否合口味。
            </p>
          </div>

          <div
            role="tablist"
            aria-label="榜单排序方式"
            className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 self-start"
          >
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                role="tab"
                aria-selected={sortKey === opt.key}
                onClick={() => setSortKey(opt.key)}
                className={`px-3.5 py-1.5 rounded-full text-xs md:text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                  sortKey === opt.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <ol className="mt-9 flex flex-col gap-3.5">
          {list.map((item, index) => (
            <li
              key={item.title}
              className="group flex flex-col sm:flex-row gap-4 p-3.5 md:p-4 rounded-lg bg-background-100/70 border border-background-200 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-start gap-4 flex-1">
                <div className="flex flex-col items-center w-9 shrink-0 pt-1">
                  <span
                    className={`font-heading text-xl md:text-2xl font-black ${
                      index < 3 ? "text-primary-500" : "text-foreground-300"
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                <div className="w-20 h-28 md:w-24 md:h-32 shrink-0 rounded-md overflow-hidden bg-background-200">
                  <img
                    src={item.cover}
                    alt={`${item.title} 漫画封面`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <span className="text-xs font-label text-foreground-500">
                      作者 · {item.author}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-label ${
                        item.status === "已完结"
                          ? "bg-accent-100 text-accent-800"
                          : "bg-primary-100 text-primary-700"
                      }`}
                    >
                      {item.status}
                    </span>
                  </div>

                  <p className="mt-1 text-xs font-label text-secondary-700">
                    {item.category}
                  </p>

                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3">
                    {item.desc}
                  </p>

                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-md bg-background-200/80 text-xs font-label text-foreground-700"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 sm:w-28 shrink-0 sm:border-l sm:border-background-200 sm:pl-4">
                <div className="text-right">
                  <div className="font-heading text-xl md:text-2xl font-bold text-primary-600">
                    {item.score}
                  </div>
                  <div className="text-xs font-label text-foreground-500">读者评分</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-label font-medium text-foreground-800">
                    {item.readers}
                  </div>
                  <div className="text-xs font-label text-foreground-500">在追人数</div>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}